import Component from '@averyano/core/src/classes/Component';
import { globalEmitter } from '@averyano/core/src/classes/GlobalEmitter';
import GSAP from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

GSAP.registerPlugin(ScrollTrigger);

const NAV_DUR = 0.6;
const NAV_EASE = 'power4.out';
const SCRUB_EASE = GSAP.parseEase('power2.in');

export default class ServicesSection extends Component {
	constructor() {
		super({
			element: '.services',
			elements: {
				wrapper: '.services__wrapper',
				items: '.services__item',
				logo: '.averyano__logo:not(.averyano__logo--overlay)',
			},
		});

		// brand colors from styles/utils/variables.scss — only ones dark enough
		// to keep the white services text readable (white/lime/steel skipped)
		this.sweepColors = [
			'#0c0b0a', // brand-black
			'#ffaa00', // brand-orange
			'#0089ea', // brand-blue
			'#00ced1', // brand-cyan
			'#ff1439', // brand-red
			'#00ce64', // brand-green
			'#ff2e96', // brand-pink
			'#6058bb', // brand-indigo
		];
		// darker shade of each sweepColor (same hue) — used for tags
		this.sweepColorsTag = [
			'#2a2725',
			'#c28100',
			'#4dabf0',
			'#00a5a8',
			'#b80e29',
			'#009448',
			'#b8216c',
			'#453f87',
		];
		this.colorIndex = 0;
		this.onLogoClickBound = this.onLogoClick.bind(this);

		this.totalWidth = 0;
		this.scrollDistance = 0;

		this.aboutFixed = false;
		// Tracks the nav's autohide state, kept in sync via nav:hide / nav:show
		// emitted by Navigation. Used to position .about while it's fixed.
		this.navIsHidden = false;
		this.navHeight = 0;

		this.onNavHideBound = this.onNavHide.bind(this);
		this.onNavShowBound = this.onNavShow.bind(this);
	}

	create() {
		super.createComponent();
		this.aboutEl = document.querySelector('.about');
		this.navEl = document.querySelector('nav');
		this.onResize();
		// this.createScrollTrigger();
		this.addEventListeners();
	}

	createScrollTrigger() {
		this.scrollTrigger = ScrollTrigger.create({
			trigger: this.element,
			start: 'bottom bottom',
			end: () => `+=${this.scrollDistance}`,
			pin: true,
			scrub: true,
			invalidateOnRefresh: true,
			onRefresh: () => this.measure(),
			onUpdate: (self) => {
				// Ease progress with power2.in so x holds near 0 at pin engage
				// and ramps up — smooths the vertical→horizontal handoff without
				// extending the pin duration (which would pin whitespace).
				const x = -this.scrollDistance * SCRUB_EASE(self.progress);
				GSAP.set(this.elements.wrapper, { x });
			},
			// Only fix on forward enter and release on backward exit past start —
			// crossing the end forward (or re-entering from after the end) keeps
			// .about pinned for the rest of the page.
			onEnter: () => this.fixAbout(),
			onLeaveBack: () => this.unfixAbout(),
		});
		GSAP.set(this.elements.wrapper, { y: 0 });
	}

	fixAbout() {
		if (!this.aboutEl || this.aboutFixed) return;
		this.aboutFixed = true;

		this.navHeight = this.navEl ? this.navEl.getBoundingClientRect().height : 0;
		this.aboutEl.style.setProperty('--data-nav-height', `${this.navHeight}px`);

		// Reserve .about's space in the flow before taking it out — otherwise
		// every sibling below (including the services pin-spacer) shifts up by
		// .about's height, and .dreams scrolls into view while services is
		// still pinned.
		const aboutHeight = this.aboutEl.getBoundingClientRect().height;
		this.aboutPlaceholder = document.createElement('div');
		this.aboutPlaceholder.style.height = `${aboutHeight}px`;
		this.aboutEl.parentNode.insertBefore(this.aboutPlaceholder, this.aboutEl);

		// Match whatever the nav's autohide state currently is — if the user
		// scrolled fast enough that the nav already hid before we pinned, .about
		// must start at y:0 instead of below the (hidden) nav.
		GSAP.set(this.aboutEl, {
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			zIndex: 5,
			y: this.navIsHidden ? 0 : this.navHeight,
		});

		GSAP.to(this.elements.wrapper, { y: 0, duration: NAV_DUR, ease: NAV_EASE });
	}

	unfixAbout() {
		if (!this.aboutEl || !this.aboutFixed) return;
		this.aboutFixed = false;
		GSAP.killTweensOf(this.aboutEl);
		GSAP.set(this.aboutEl, {
			y: 0,
			clearProps: 'position,top,left,right,zIndex,transform,y',
		});
		this.aboutEl.style.removeProperty('--data-nav-height');

		if (this.aboutPlaceholder) {
			this.aboutPlaceholder.remove();
			this.aboutPlaceholder = null;
		}
	}

	onNavHide() {
		this.navIsHidden = true;
		if (!this.aboutFixed || !this.aboutEl) return;
		GSAP.to(this.aboutEl, { y: 0, duration: NAV_DUR, ease: NAV_EASE });
		GSAP.to(this.elements.wrapper, { y: 0, duration: NAV_DUR, ease: NAV_EASE });
		console.log('nav hide');
	}

	onNavShow() {
		this.navIsHidden = false;
		if (!this.aboutFixed || !this.aboutEl) return;
		GSAP.to(this.aboutEl, {
			y: this.navHeight,
			duration: NAV_DUR,
			ease: NAV_EASE,
		});
		GSAP.to(this.elements.wrapper, {
			y: this.navHeight / 1.618,
			duration: NAV_DUR,
			ease: NAV_EASE,
		});

		console.log('nav show');
	}

	measure() {
		const wrapperWidth = this.elements.wrapper.getBoundingClientRect().width;
		const styles = window.getComputedStyle(this.element);
		const padLeft = parseFloat(styles.paddingLeft) || 0;
		const padRight = parseFloat(styles.paddingRight) || 0;
		this.totalWidth = wrapperWidth;
		this.scrollDistance = Math.max(
			0,
			wrapperWidth - (window.innerWidth - padLeft - padRight)
		);
	}

	onLogoClick() {
		this.colorIndex = (this.colorIndex + 1) % this.sweepColors.length;
		this.element.style.setProperty(
			'--services-color',
			this.sweepColors[this.colorIndex]
		);
		this.element.style.setProperty(
			'--services-tag-color',
			this.sweepColorsTag[this.colorIndex]
		);
	}

	addEventListeners() {
		this.$on('nav:hide', this.onNavHideBound);
		this.$on('nav:show', this.onNavShowBound);
		this.elements.logo?.addEventListener('click', this.onLogoClickBound);
	}

	removeEventListeners() {
		this.off('nav:hide', this.onNavHideBound);
		this.off('nav:show', this.onNavShowBound);
		globalEmitter.off('nav:hide', this.onNavHideBound);
		globalEmitter.off('nav:show', this.onNavShowBound);
		this.elements.logo?.removeEventListener('click', this.onLogoClickBound);
	}

	onResize() {
		this.measure();
		if (this.aboutFixed && this.navEl) {
			this.navHeight = this.navEl.getBoundingClientRect().height;
			this.aboutEl.style.setProperty(
				'--data-nav-height',
				`${this.navHeight}px`
			);
			if (!this.navIsHidden) {
				GSAP.set(this.aboutEl, { y: this.navHeight });
			}
		}
	}

	destroy() {
		this.scrollTrigger?.kill();
		this.unfixAbout();
		this.removeEventListeners();
	}
}
